import * as React from "react";
import classNames from "classnames";

import Breadcrumbs from "./Breadcrumbs";
import ExpandableRecordList from "./ExpandableRecordList";
import { RecordDetails } from "./landing_types";
import { capitalizeFirstLetter, SEO_URLS } from "./landing_utils";
import {
  QueryTopicLinks,
  QueryTopicLinkspam,
  SourceLinks,
  SourceLinkSpam,
  TopicLinks,
  TopicLinkSpam,
} from "./LandingPageLinks";
import { Footer, Header, PromoSection, usePageViewLogger } from "./shared";

import css from "./LandingPage.module.scss";

const TAB_SELECTOR = "records";

type LandingPageConfig = {
  records: RecordDetails[];
  object_type: string;
  category_type: string;
  descriptor: string;
  show_tabs?: {
    tenders: boolean;
    recent_awards: boolean;
    upcoming_expiries: boolean;
  };
};

export type LandingPageProps = {
  config: LandingPageConfig;
  app_root_url: string;
  topic_linkspam?: TopicLinks;
  source_linkspam?: SourceLinks;
  query_based_topic_linkspam?: QueryTopicLinks;
  current_page: number;
  total_results_count: number;
  extra_params_for_paging?: Record<string, string>;
};

type ObjTypeInfo = {
  humanised: string;
  original: string;
  adjective: string;
  object: string;
  tabLabel: string;
};

export function getObjTypeInfo(objType: string): ObjTypeInfo {
  const info: Partial<ObjTypeInfo> = { original: objType };
  let [adjective, obj] = objType.split("_");
  if (!obj) {
    obj = adjective;
    adjective = "";
  }
  switch (objType) {
    case "recent_awards":
    case "awarded_contracts":
      info.adjective = "awarded";
      info.object = "contracts";
      info.humanised = "awarded contracts";
      info.tabLabel = "recent awards";
      break;
    case "tenders":
      info.adjective = "";
      info.object = "tenders";
      info.humanised = "tenders";
      info.tabLabel = "open opportunities";
      break;
    case "upcoming_expiries":
    case "expiring_contracts":
      info.adjective = "expiring";
      info.object = "contracts";
      info.humanised = "expiring contracts";
      info.tabLabel = "upcoming contract expiries";
      break;
    default:
      throw `invalid object type ${objType}`;
  }
  return info as ObjTypeInfo;
}

const tabTenders = ["Open opportunities", ["tenders"]];
const tabRecentAwards = ["Recent awards", ["recent_awards", "awarded_contracts"]];
const tabUpcomingExpiries = [
  "Upcoming contract expiries",
  ["upcoming_expiries", "expiring_contracts"],
];

/**
 * This page laregly copies @see BuyerLandingPage
 * so maybe at some point this page and that one can be refactored. Also it was easier to separate this page and
 * the original landing page for the sake of easily being able to separate logic without risk of mistakenly
 * changing the original landing page logic
 * @param param0
 * @returns
 */
function LandingPage({
  config,
  app_root_url,
  topic_linkspam,
  source_linkspam,
  current_page,
  total_results_count,
  extra_params_for_paging,
  query_based_topic_linkspam,
}: LandingPageProps): JSX.Element {
  const { records, descriptor, category_type, show_tabs: showTabs } = config;
  const { humanised: humanObjectType } = getObjTypeInfo(config.object_type);

  // some profiles won't actually have any open tenders (the default), so we should check which of
  // the other record types are present and set the first one to active
  const activeRecordType = config.object_type;

  // Send page view event
  usePageViewLogger();

  const otherPagesTabs = React.useMemo(() => {
    const tabs = showTabs
      ? [
          showTabs.tenders ? tabTenders : [],
          showTabs.recent_awards ? tabRecentAwards : [],
          showTabs.upcoming_expiries ? tabUpcomingExpiries : [],
        ].filter((val) => val.length !== 0)
      : [tabTenders, tabRecentAwards, tabUpcomingExpiries];

    return (
      <ul className={css.otherPages} id={TAB_SELECTOR} key={activeRecordType}>
        {tabs.map(([label, aliases]) => {
          const active = aliases.includes(activeRecordType);
          return (
            <li key={`${label}`} className={active ? css.active : undefined}>
              {active ? (
                <div className={css.tab}>{label}</div>
              ) : (
                <a href={`?type=${aliases[0]}`} className={classNames(css.tab, css.inactiveTab)}>
                  {label}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    );
  }, [activeRecordType, showTabs]);

  const breadcrumbs = React.useMemo(() => {
    const cat = category_type == "cpv_topics" ? "topics" : category_type;
    return [
      {
        id: "home",
        name: "Home",
        href: "/",
      },
      {
        id: config.object_type,
        name: capitalizeFirstLetter(humanObjectType),
        href: SEO_URLS.tenderSearch,
      },
      {
        id: cat,
        name: capitalizeFirstLetter(cat),
        href: `/explore/search/${cat}`,
      },
      {
        id: descriptor,
        name: capitalizeFirstLetter(descriptor),
      },
    ];
  }, [category_type, descriptor, config.object_type, humanObjectType]);

  const promoSubjectName = <em className={css.promoSubjectName}>{descriptor}</em>;

  const linkspamSection = React.useMemo(() => {
    const subj = <em className={css.subjectName}>{descriptor}</em>;

    switch (category_type) {
      case "sources":
        if (source_linkspam) {
          return (
            <SourceLinkSpam
              subjectName={subj}
              descriptor={descriptor}
              app_root_url={app_root_url}
              source_linkspam={source_linkspam}
            />
          );
        }
        break;
      case "cpv_topics":
        if (topic_linkspam) {
          return (
            <TopicLinkSpam
              subjectName={subj}
              descriptor={descriptor}
              app_root_url={app_root_url}
              topic_linkspam={topic_linkspam}
            />
          );
        }
        break;
      case "topics":
        if (query_based_topic_linkspam) {
          return <QueryTopicLinkspam links={query_based_topic_linkspam} />;
        }
    }
    return null;
  }, [
    app_root_url,
    category_type,
    source_linkspam,
    topic_linkspam,
    descriptor,
    query_based_topic_linkspam,
  ]);

  // Not used if category type is sources
  const tabsDescription = React.useMemo(() => {
    const visibleTabs = [
      // Need to hide ONLY if value is false as undefined means show
      showTabs?.tenders !== false && "open tenders",
      showTabs?.recent_awards !== false && "recent contract awards",
      showTabs?.upcoming_expiries !== false && "upcoming contract expiries",
    ].filter(Boolean);
    if (visibleTabs.length === 0) {
      return "";
    }
    if (visibleTabs.length === 1) {
      return visibleTabs[0];
    }
    return visibleTabs.slice(0, -1).join(", ") + " and " + visibleTabs[visibleTabs.length - 1];
  }, [showTabs]);

  return (
    <div className={css.landingPage}>
      <Header appRootUrl={app_root_url} />
      <main>
        <Breadcrumbs items={breadcrumbs} />
        <section className={css.headerSection}>
          <div>
            {category_type == "sources" ? (
              <>
                <h1>
                  <em className={css.subjectName}>{capitalizeFirstLetter(descriptor)}</em> tenders &
                  contracts
                </h1>
                <p>
                  Stotles aggregates thousands of tenders and contracts from different government
                  sources, including {descriptor}. Search through {descriptor}'s open tenders,
                  recent contract awards and upcoming contract expiries below.
                </p>
              </>
            ) : (
              <>
                <h1>
                  Tenders & contracts relating to <em className={css.subjectName}>{descriptor}</em>
                </h1>
                <p>
                  Find <span className={css.bodyBuyerName}>{descriptor}</span> opportunities in
                  seconds below. Search through thousands of{" "}
                  <span className={css.bodyBuyerName}>{descriptor}</span> {tabsDescription}.
                  Bookmark the tender and begin your bidding process with Stotles.
                </p>
              </>
            )}

            <PromoSection
              text={<>Manage tenders relating to {promoSubjectName} in seconds with Stotles.</>}
              className={css.promo}
              appRootUrl={app_root_url}
            />
          </div>

          <div className={css.transparentSquare} />
        </section>
        <section>
          <h2 className={css.header}>
            <em className={css.subjectName}>{capitalizeFirstLetter(descriptor)}</em> tenders &
            contracts
          </h2>
          {otherPagesTabs}
          <div id="records">
            <ExpandableRecordList
              records={records}
              hideBuyer
              currentPage={current_page}
              totalResultsCount={total_results_count}
              extraParamsForPaging={extra_params_for_paging}
              promoSection={
                <PromoSection
                  text={
                    <>
                      Get alerts on {category_type === "sources" && "the latest "}
                      {promoSubjectName} tenders & contracts straight to your inbox
                    </>
                  }
                  appRootUrl={app_root_url}
                  large={true}
                />
              }
            />
          </div>
        </section>
        {linkspamSection}
      </main>
      <Footer />
    </div>
  );
}

export default LandingPage;
